<script setup>
import { computed, onBeforeUnmount, onMounted } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { FrappeUIProvider } from 'frappe-ui'
import { canAccessRoute } from './rbac'
import { refreshPermissionsIfChanged, session } from './session'
import AppShell from './layouts/AppShell.vue'
import AuthLayout from './layouts/AuthLayout.vue'

const route = useRoute()
const router = useRouter()
const isAuth = computed(() => route.meta.layout === 'auth' || route.name === 'Login')

async function syncPermissions() {
  if (!session.boot?.authenticated || route.meta.public) return
  const changed = await refreshPermissionsIfChanged()
  if (!changed) return
  const current = router.currentRoute.value
  if (!canAccessRoute(current.meta)) {
    const home = session.boot.home_route || '/'
    router.replace(home && home !== '/login' ? home : '/')
  }
}

function onFocus() {
  if (document.visibilityState !== 'hidden') syncPermissions()
}

onMounted(() => {
  window.addEventListener('focus', onFocus)
  document.addEventListener('visibilitychange', onFocus)
  router.afterEach(() => {
    syncPermissions()
  })
})

onBeforeUnmount(() => {
  window.removeEventListener('focus', onFocus)
  document.removeEventListener('visibilitychange', onFocus)
})
</script>

<template>
  <FrappeUIProvider>
    <div class="min-h-screen bg-surface-base text-ink-gray-9">
      <div
        v-if="!session.loaded || session.loading"
        class="flex min-h-screen items-center justify-center"
      >
        <span class="lucide-loader-circle size-6 animate-spin text-ink-gray-5" aria-hidden="true" />
      </div>
      <template v-else>
        <AuthLayout v-if="isAuth">
          <router-view />
        </AuthLayout>
        <AppShell v-else-if="session.boot.authenticated">
          <router-view />
        </AppShell>
        <router-view v-else />
      </template>
    </div>
  </FrappeUIProvider>
</template>
